import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Brand from "@/components/Brand";
import { ServiceIcon } from "@/components/ServiceIcon";
import {
  LogIn, UserPlus, ArrowRight, Zap, ShieldCheck, RefreshCw, Globe2,
  Clock, TrendingUp, Sparkles, MessageSquare, ChevronRight, Compass
} from "lucide-react";

export default function Landing() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen brand-bg text-white">
      {/* Sticky top nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 md:px-6 h-16">
          <Link to="/"><Brand size="sm" /></Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-white/70">
            <a href="#features" className="hover:text-white transition-colors">Özellikler</a>
            <a href="#services" className="hover:text-white transition-colors">Servisler</a>
            <a href="#how" className="hover:text-white transition-colors">Nasıl çalışır</a>
            <a href="#faq" className="hover:text-white transition-colors">SSS</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/panel" data-testid="landing-panel-cta" className="btn-brand font-bold text-sm px-4 py-2 rounded-xl inline-flex items-center gap-1.5">
                Panele Git <ChevronRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login-btn" className="w-10 h-10 md:w-auto md:px-4 md:py-2 md:h-10 md:rounded-xl md:text-sm border border-white/20 rounded-lg flex items-center justify-center gap-1.5 text-white/90 hover:border-[hsl(43,74%,55%)]/60 transition-colors">
                  <LogIn className="w-4 h-4" /> <span className="hidden md:inline">Giriş</span>
                </Link>
                <Link to="/register" data-testid="landing-register-btn" className="w-10 h-10 md:w-auto md:px-4 md:py-2 md:h-10 md:rounded-xl md:text-sm rounded-lg flex items-center justify-center gap-1.5 btn-brand font-bold">
                  <UserPlus className="w-4 h-4" /> <span className="hidden md:inline">Kayıt Ol</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO — smsonay-style but with our brand */}
      <section className="relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-5 md:px-6 pt-16 pb-14 md:pt-24 md:pb-16 text-center">
          <div className="inline-flex items-center gap-2 border border-[hsl(43,74%,55%)]/40 bg-[hsl(43,74%,55%)]/10 text-[hsl(43,74%,68%)] px-4 py-1.5 rounded-full text-xs md:text-sm font-bold mb-8" data-testid="hero-badge">
            <Zap className="w-3.5 h-3.5" /> Yeni · Tüm ülkeler aktif, anlık SMS alabilirsiniz.
          </div>

          <h1 className="font-display font-black text-4xl md:text-6xl leading-[1.05] tracking-tight mb-6">
            Kişisel Numaranızı Gizleyin<br />
            <span className="brand-gradient">Sanal Numara ile</span> SMS<br />
            Onay Yapın
          </h1>

          <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            100'den fazla uygulama ve web servisi için tek kullanımlık sanal numara kiralayın.
            Kendi numaranızı paylaşmadan saniyeler içinde hesap doğrulama kodunuzu alın.
          </p>

          <div className="flex flex-col gap-3 max-w-md mx-auto mb-8">
            <Link
              to={user ? "/sms-ai" : "/register"}
              data-testid="hero-cta-primary"
              className="btn-brand font-bold text-base py-4 rounded-full inline-flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> SMS AI <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="#services"
              data-testid="hero-cta-secondary"
              className="border-2 border-[hsl(43,74%,55%)]/60 text-white font-bold text-base py-3.5 rounded-full inline-flex items-center justify-center gap-2 hover:bg-[hsl(43,74%,55%)]/10 hover:border-[hsl(43,74%,55%)] transition-colors"
              onClick={(e) => { e.preventDefault(); document.getElementById("services")?.scrollIntoView({ behavior: "smooth" }); }}
            >
              <Compass className="w-4 h-4" /> Servisleri Keşfet
            </Link>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-white/60 text-xs md:text-sm">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[hsl(142,71%,55%)]" /> Güvenli & Anonim</span>
            <span className="inline-flex items-center gap-1.5"><RefreshCw className="w-4 h-4 text-[hsl(217,91%,68%)]" /> 20dk Otomatik İade</span>
            <span className="inline-flex items-center gap-1.5"><Globe2 className="w-4 h-4 text-[hsl(0,84%,68%)]" /> {countries.length || "50"}+ Ülke Desteği</span>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
          <div className="max-w-2xl mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Neden SmsKapıda?</div>
            <h2 className="font-display font-black text-3xl md:text-5xl leading-tight">Şeffaf, hızlı ve iade garantili.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Feature icon={<RefreshCw className="w-5 h-5" />} title="20dk Otomatik İade" desc="SMS gelmezse 20. dakikada bakiyeniz otomatik iade edilir. Risk sıfır." accent="hsl(142,71%,55%)" />
            <Feature icon={<Clock className="w-5 h-5" />} title="4dk İptal Kontrolü" desc="4. dakikadan itibaren manuel iptal edip iadenizi anında alabilirsiniz." accent="hsl(38,92%,55%)" />
            <Feature icon={<TrendingUp className="w-5 h-5" />} title="Kademeli Fiyatlandırma" desc="Her ülke ve servis için canlı stok ve 3-5 farklı fiyat seçeneği. En uygun kademeyi seç." accent="hsl(217,91%,60%)" />
            <Feature icon={<Globe2 className="w-5 h-5" />} title="150+ Ülke" desc="Türkiye, ABD, Rusya, Almanya, İngiltere, Hindistan ve daha fazlası." accent="hsl(280,80%,65%)" />
            <Feature icon={<ShieldCheck className="w-5 h-5" />} title="Güvenli & Anonim" desc="Tek kullanımlık numaralar. Kişisel bilgileriniz asla paylaşılmaz." accent="hsl(0,84%,60%)" />
            <Feature icon={<Sparkles className="w-5 h-5" />} title="SMS AI Asistan" desc="Fiyatları karşılaştıran, en uygun ülkeyi öneren dahili yardımcı." accent="hsl(190,90%,55%)" />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="border-t border-white/10 bg-black/40">
        <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Popüler servisler</div>
              <h2 className="font-display font-black text-3xl md:text-5xl">100+ platform desteklenir.</h2>
            </div>
            <Link to={user ? "/buy" : "/register"} className="text-sm text-[hsl(217,91%,68%)] hover:text-white transition-colors inline-flex items-center gap-1" data-testid="services-cta">
              Tüm servisleri gör <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-10 gap-3">
            {services.map((s) => (
              <Link key={s.code} to={user ? "/buy" : "/register"} className="border border-white/10 bg-[hsl(0,0%,7%)]/70 hover:border-[hsl(217,91%,60%)]/60 rounded-2xl p-3 flex flex-col items-center gap-2 transition-colors">
                <ServiceIcon code={s.code} size="md" />
                <div className="text-[10px] text-white/70 uppercase tracking-wider text-center leading-tight truncate w-full">{s.name.split("/")[0]}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
          <div className="max-w-2xl mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Nasıl çalışır</div>
            <h2 className="font-display font-black text-3xl md:text-5xl leading-tight">3 adımda numara al.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Step n={1} title="Kayıt ol ve bakiye yükle" desc="Ücretsiz üye ol. IBAN veya kripto (USDT / BTC / ETH) ile bakiye yükle." />
            <Step n={2} title="Servis + ülkeyi seç" desc="Uygulamayı ve ülkeyi seçtikten sonra fiyat kademelerinden dilediğini seç." />
            <Step n={3} title="SMS kodunu al" desc="Numara anında verilir. SMS 1-3 dakikada gelir. Gelmezse 20. dakikada tam iade otomatik yapılır." />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-t border-white/10 bg-black/40">
        <div className="max-w-6xl mx-auto px-5 md:px-6 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatPill big="100+" small="Servis" />
            <StatPill big="150+" small="Ülke" />
            <StatPill big="20dk" small="İade süresi" />
            <StatPill big="7/24" small="Canlı destek" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-white/10">
        <div className="max-w-3xl mx-auto px-5 md:px-6 py-16 md:py-24">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">SSS</div>
            <h2 className="font-display font-black text-3xl md:text-5xl leading-tight">Sıkça sorulanlar.</h2>
          </div>
          <div className="space-y-2">
            {FAQS.map((f) => (
              <details key={f.q} className="border border-white/10 bg-[hsl(0,0%,7%)]/70 rounded-2xl p-4 group">
                <summary className="cursor-pointer font-bold text-white/90 flex items-center justify-between">
                  {f.q}
                  <span className="text-white/40 group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <p className="text-white/60 text-sm mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-black/40">
        <div className="max-w-3xl mx-auto px-5 md:px-6 py-16 md:py-20 text-center">
          <MessageSquare className="w-10 h-10 mx-auto mb-4 text-[hsl(217,91%,68%)]" />
          <h2 className="font-display font-black text-3xl md:text-5xl leading-tight mb-4">
            Bugün ilk numaranı al.
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Ücretsiz kayıt, saniyelerde teslim, 20dk iade garantisi.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/register" className="btn-brand font-bold text-sm px-6 py-3.5 rounded-full inline-flex items-center gap-2" data-testid="cta-register">
              Hemen Ücretsiz Kayıt Ol <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="border-2 border-[hsl(43,74%,55%)]/60 hover:bg-[hsl(43,74%,55%)]/10 hover:border-[hsl(43,74%,55%)] font-bold text-sm px-6 py-3.5 rounded-full inline-flex items-center gap-2 transition-colors" data-testid="cta-login">
              <LogIn className="w-4 h-4" /> Giriş Yap
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-5 md:px-6 py-10 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Brand size="sm" />
            <div className="text-xs text-white/40 tracking-[0.2em] uppercase">© 2026 SmsKapıda · Tüm hakları saklıdır</div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-white/50 border-t border-white/5 pt-6">
            <a href="#features" className="hover:text-white transition-colors">Özellikler</a>
            <a href="#faq" className="hover:text-white transition-colors">SSS</a>
            <Link to="/legal/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Kullanım Koşulları</Link>
            <Link to="/legal/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Gizlilik Politikası</Link>
            <Link to="/legal/kvkk" className="hover:text-white transition-colors" data-testid="footer-kvkk">KVKK</Link>
            <Link to="/legal/refund" className="hover:text-white transition-colors" data-testid="footer-refund">İade Politikası</Link>
            <a href="https://t.me/smskapida" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">İletişim</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

const FAQS = [
  { q: "SMS kodu ne kadar sürede gelir?", a: "Çoğu servis için 1-3 dakika içinde ulaşır. Yoğun saatlerde 5-10 dakikaya kadar uzayabilir. 20 dakikada gelmezse tam iade otomatik yapılır." },
  { q: "Numara aldıktan sonra ne zaman iptal edebilirim?", a: "Satın aldığın andan itibaren 4. dakikada 'İptal & İade' butonu aktifleşir. 20. dakikada kod gelmezse otomatik iade edilir." },
  { q: "SMS kodu geldikten sonra iade alabilir miyim?", a: "Hayır. Kodu aldığınız anda numaranın kullanım hakkı size geçer, iade yapılamaz. Bu, sağlayıcının politikasıdır." },
  { q: "Hangi ödeme yöntemleri kabul ediliyor?", a: "Havale/EFT (Türkiye IBAN) ve kripto para (USDT TRC20, Bitcoin, Ethereum). Bakiye admin onayından sonra hesabınıza yansır." },
  { q: "Aynı numarayı tekrar kullanabilir miyim?", a: "Sanal numaralarımız tek kullanımlıktır. Her yeni aktivasyon için yeni bir numara satın alınız." },
  { q: "Fiyatlar neden kademeli?", a: "Sağlayıcı ağında aynı servis+ülke için farklı özelliklerde (öncelik, hız, stok) numaralar var. Bütçenize ve stoğa göre uygun kademeyi seçiyorsunuz." },
];

function Feature({ icon, title, desc, accent }) {
  return (
    <div className="border border-white/10 bg-[hsl(0,0%,7%)]/70 rounded-2xl p-6 hover:border-white/25 transition-colors">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${accent}22`, color: accent }}>
        {icon}
      </div>
      <div className="font-display font-bold text-lg mb-2">{title}</div>
      <div className="text-white/60 text-sm leading-relaxed">{desc}</div>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="border border-white/10 bg-[hsl(0,0%,7%)]/70 rounded-2xl p-6">
      <div className="w-10 h-10 rounded-full border-2 border-[hsl(217,91%,60%)] text-[hsl(217,91%,68%)] font-black font-mono flex items-center justify-center mb-4">{n}</div>
      <div className="font-display font-bold text-lg mb-2">{title}</div>
      <div className="text-white/60 text-sm leading-relaxed">{desc}</div>
    </div>
  );
}

function StatPill({ big, small }) {
  return (
    <div className="border border-white/10 bg-white/[0.03] rounded-2xl p-6 text-center">
      <div className="font-display font-black text-3xl md:text-4xl brand-gradient">{big}</div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">{small}</div>
    </div>
  );
}
